import React, { useState } from 'react';
import styles from './PaymentTokens.module.scss';

export interface PaymentTokenItem {
  id: string;
  token: string;
  amount: number;
  currency: string;
  status: 'généré' | 'consommé';
  createdAt: string;
  expiresAt: string;
  description?: string;
  kwhValue?: number;
  dayValue?: number;
  typePayment?: string;
}

export interface PaymentTokenItemProps {
  token: PaymentTokenItem;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function PaymentTokenItem({ token }: PaymentTokenItemProps) {
  const [showFullToken, setShowFullToken] = useState(false);
  const getStatusIcon = (status: PaymentTokenItem['status']) => {
    switch (status) {
      case 'généré':
        return '●';
      case 'consommé':
        return '✓';
      default:
        return '?';
    }
  };

  const getStatusClass = (status: PaymentTokenItem['status']) => {
    switch (status) {
      case 'généré': return styles.statusPending;
      case 'consommé': return styles.statusCompleted;
      default: return '';
    }
  };

  const getStatusText = (status: PaymentTokenItem['status']) => {
    switch (status) {
      case 'généré':
        return 'Généré';
      case 'consommé':
        return 'Consommé';
      default:
        return 'Inconnu';
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    const upperCurrency = currency.toUpperCase();
    // XOF/XAF are not well supported by Intl, display manually
    if (upperCurrency === 'XOF' || upperCurrency === 'XAF') {
      return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
    }
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: upperCurrency
      }).format(amount);
    } catch {
      return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + upperCurrency;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatToken = (token: string) => {
    if (!token || token.length <= 12) return token || '';
    return `${token.substring(0, 8)}...${token.substring(token.length - 4)}`;
  };

  const formatKwhValue = (kwhValue?: number) => {
    if (kwhValue === undefined) return null;
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(kwhValue);
  };

  const getTypeLabel = (type?: string): string => {
    if (!type) return 'Paiement';
    switch (type.toLowerCase()) {
      case 'cleartamper': return 'Clear Tamper';
      case 'clearcredit': return 'Clear Crédit';
      case 'removerestriction': return 'Levée restriction';
      case 'lockproduct': return 'Verrouillage';
      case 'unlockproduct': return 'Déverrouillage';
      case 'gift': return 'Code gratuit';
      case 'credit': return 'Crédit';
      case 'reduction': return 'Réduction';
      case 'angaza-manual': return 'Angaza Manuel';
      case 'auto_sync': return 'Sync auto';
      default: return type;
    }
  };

  const getTypeStyleClass = (type?: string): string => {
    if (!type) return styles.typePayment;
    switch (type.toLowerCase()) {
      case 'cleartamper':
      case 'clearcredit':
        return styles.typeMaintenance;
      case 'gift':
      case 'reduction':
      case 'credit':
        return styles.typeGift;
      case 'lockproduct':
        return styles.typeLock;
      case 'unlockproduct':
      case 'removerestriction':
        return styles.typeUnlock;
      default:
        return styles.typePayment;
    }
  };




  return (
    <div className={styles.tokenEntry}>
      <div className={styles.tokenHeader}>
        <div className={styles.tokenInfo}>
          <span className={styles.amount}>{formatAmount(token.amount, token.currency)}</span>
          <div className={styles.energyDetails}>
            <span className={getTypeStyleClass(token.typePayment)}>
              {getTypeLabel(token.typePayment)}
            </span>
            {token.kwhValue !== undefined && token.kwhValue > 0 && (
              <span className={styles.energyValue}>
                {formatKwhValue(token.kwhValue)} kWh
              </span>
            )}
            {token.dayValue !== undefined && token.dayValue > 0 && (
              <span className={styles.dayValue}>
                {token.dayValue} jour{token.dayValue > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className={`${styles.status} ${getStatusClass(token.status)}`}>
          <span className={styles.statusIcon}>{getStatusIcon(token.status)}</span>
          <span className={styles.statusText}>{getStatusText(token.status)}</span>
        </div>
      </div>
      
      {token.description && (
        <div className={styles.description}>
          {token.description}
        </div>
      )}
      
      <div className={styles.tokenFooter}>
        <div className={styles.timestamp}>
          <span>{formatTimestamp(token.createdAt)}</span>
        </div>
        <div className={styles.tokenContainer}>
          <span className={styles.tokenValue}>
            {showFullToken ? token.token : formatToken(token.token)}
          </span>
          <button 
            className={styles.toggleButton}
            onClick={() => setShowFullToken(!showFullToken)}
            title={showFullToken ? "Masquer le token" : "Afficher le token complet"}
          >
            {showFullToken ? "Masquer" : "Afficher"}
          </button>
        </div>
      </div>
    </div>
  );
}
